import React, { useState } from 'react';
import './App.css';
import NewPage from './NewPage' ;

// Import data from separate files
import { aItems as aItems3plus1, bItems as bItems3plus1, cItems as cItems3plus1 } from './data/sampleData3plus1';
import { aItems as aItems2years, bItems as bItems2years, cItems as cItems2years } from './data/sampleData2years';
import { aItems as aItemsDoctoral, bItems as bItemsDoctoral, cItems as cItemsDoctoral, dItems as dItemsDoctoral } from './data/sampleDataDoctoral';
import ItemList from './components/ItemList';

function App() {
  // Add state for active table
  const [currentPage, setCurrentPage ] = useState('main');
  const [activeTable, setActiveTable] = useState('3+1'); // '3+1', '2-years', 'doctoral'
  
  const [selectedA, setSelectedA] = useState(null);
  const [selectedB, setSelectedB] = useState(null);
  const [selectionOrder, setSelectionOrder] = useState([]); // Track selection order: 'A' or 'B'
  const [showCItems, setShowCItems] = useState(false);
  const [selectedC, setSelectedC] = useState(null);
  const [selectedD, setSelectedD] = useState(null); // Add state for selected D item
  const [showDItems, setShowDItems] = useState(true); // Add state for showing D items
  const [selectedInfo, setSelectedInfo] = useState("");
  const [filteredAItems, setFilteredAItems] = useState(null); // Add state for filtered A items
  const [showBItems, setShowBItems] = useState(true); // Add this new state for B-column visibility
  const [isModalOpen, setIsModalOpen] =useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  
  // Add search functionality like NewPage.js
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Search function (simplified for App.js)
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Simple search through current data
    const { aItems, bItems, cItems } = getActiveData();
    const results = [];
    
    // Search in A items (universities)
    aItems.forEach(item => {
      if (item.name.toLowerCase().includes(query.toLowerCase())) {
        results.push({
          type: 'university',
          university: item.name,
          match: item.name,
          data: item
        });
      }
    });
    
    // Search in B items (majors)
    bItems.forEach(item => {
      if (item.name.toLowerCase().includes(query.toLowerCase())) {
        results.push({
          type: 'major',
          university: 'Various',
          match: item.name,
          data: item
        });
      }
    });
    
    // Search in C items (courses/programs)
    cItems.forEach(item => {
      if (item.name && item.name.toLowerCase().includes(query.toLowerCase())) {
        results.push({
          type: 'course',
          university: 'Various',
          match: item.name,
          data: item
        });
      }
    });
    
    setSearchResults(results.slice(0, 10)); // Limit to 10 results
    setShowSearchResults(true);
  };
  
  const handleSearchBlur = () => {
    setTimeout(() => setShowSearchResults(false), 200);
  };
  
  const handleSearchResultClick = (result) => {
    if (result.type === 'university') {
      setSelectedA(result.data);
      handleAItemClick(result.data);
    } else if (result.type === 'major') {
      setSelectedB(result.data);
      handleBItemClick(result.data);
    } else if (result.type === 'course') {
      setSelectedC(result.data);
      handleCItemClick(result.data);
    }
    setSearchQuery('');
    setShowSearchResults(false);
  };
  
  const getResultTypeLabel = (type) => {
    switch(type) {
      case 'university': return '大学';
      case 'major': return '专业';
      case 'course': return '课程';
      default: return '其他';
    }
  };
  
  const openModal = (imageSrc) => {
    setModalImageSrc(imageSrc);
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goToNewPage = () => {
    setCurrentPage('new');
  };

  const goToMainPage = () => {
    setCurrentPage('main');
  };

  if (currentPage === 'new')
  {
    return <NewPage onBackToMain={goToMainPage} />;
  }

  // Get the active data based on the selected table
  const getActiveData = () => {
    switch(activeTable) {
      case '3+1':
        return { aItems: aItems3plus1, bItems: bItems3plus1, cItems: cItems3plus1 };
      case '2-years':
        return { aItems: aItems2years, bItems: bItems2years, cItems: cItems2years };
      case 'doctoral':
        return { 
          aItems: aItemsDoctoral, 
          bItems: bItemsDoctoral, 
          cItems: cItemsDoctoral,
          dItems: dItemsDoctoral // Add D items
        };
      default:
        return { aItems: aItems3plus1, bItems: bItems3plus1, cItems: cItems3plus1 };
    }
  };

  // Get current data
  const { aItems, bItems, cItems, dItems } = getActiveData();

  // Helper to get connected A items for a selected B
  const getConnectedAItems = (bId) => {
    return aItems.filter(a => bItems.some(b => b.id === bId && b.connections.some(conn => conn.a === a.id)));
  };

  // Update A-item click handler
  const handleAItemClick = (item) => {
    if (selectedA?.id === item.id) {
      setSelectedA(null);
      setSelectedB(null);
      setShowCItems(false);
      setShowBItems(true);
      setSelectionOrder([]);
      return;
    }
    setSelectedA(item);
    setSelectedInfo(item.info);
    // Always reset C-item selection when selecting a university
    setSelectedC(null);
    setShowCItems(false);
    
    if (activeTable === 'doctoral') {
      // For doctoral table: always show B items after A selection
      setShowBItems(true);
      setSelectionOrder(['A']);
      // Reset B and C selections when selecting new A
      setSelectedB(null);
      setShowCItems(false);
    } else {
      // Original logic for other tables
      // Track selection order
      if (!selectedB) {
        setSelectionOrder(['A']);
      } else if (!selectionOrder.includes('A')) {
        setSelectionOrder([...selectionOrder, 'A']);
      }
      // Don't reset selectedB when selecting a new A item
      
      // Check if we should show C items based on current B selection and new A selection
      if (selectedB && selectedB.connections.some(conn => conn.a === item.id)) {
        setShowCItems(true);
      } else {
        setShowCItems(false);
      }
      setShowBItems(true);
    }
  };
  

  const getFilteredBItems = () => {
    if (activeTable === 'doctoral') {
      // For doctoral table: A must be selected to show B items
      if (selectedA) {
        let filteredB = bItems.filter(b => b.connections.some(conn => conn.a === selectedA.id));
        // If D is also selected, further filter by D
        if (selectedD) {
          filteredB = filteredB.filter(b => b.connections.some(conn => conn.d === selectedD.id));
        }
        return filteredB;
      }
      return [];
    } else {
      // Original logic for other tables
      if (selectedA) {
        return bItems.filter(b => b.connections.some(conn => conn.a === selectedA.id));
      }
      if (selectedD) {
        return bItems.filter(b => b.connections.some(conn => conn.d === selectedD.id));
      }
      return [];
    }
  };

  
  const handleBItemClick = (item) => {
  if (selectedB?.id === item.id) {
    setSelectedB(null);
    setShowCItems(false);
    setShowBItems(true); // Show B-column again when deselecting
    setSelectionOrder([]);
    return;
  }
  setSelectedB(item);
  
  if (activeTable === 'doctoral') {
    // For doctoral table: only allow B selection if A is already selected
    if (selectedA) {
      setShowBItems(false); // Hide B-column
      setShowCItems(true);  // Show C-column
    }
  } else {
    // Original logic for other tables
    // Track selection order
    if (!selectedA) {
      setSelectionOrder(['B']);
    } else if (!selectionOrder.includes('B')) {
      setSelectionOrder([...selectionOrder, 'B']);
    }
    setShowCItems(selectedA && item.connections.some(conn => conn.a === selectedA.id));
  }
  };
    
    // Get C items based on A+B or B+A combination
  const getCItems = () => {
    if (!selectedA || !selectedB) return [];
    
    let filteredCItems;
    if (activeTable === 'doctoral') {
      // For doctoral data, filter based on University, Expertise, and D_ID properties
      filteredCItems = cItems.filter(item => 
        item.University === selectedA.id && 
        item.Expertise === selectedB.id &&
        (!selectedD || item.D_ID === selectedD.id) // Only show if D_ID matches selected D-item
      );
    } else {
      // Original logic for other tables
      const key1 = `${selectedB.id}_${selectedA.id}`;
      const key2 = `${selectedA.id}_${selectedB.id}`;
      filteredCItems = cItems.filter(item => item.info === key1 || item.info === key2);
    }
    
    // If a C item is selected, show only that item; otherwise show all filtered items
    if (selectedC) {
      return filteredCItems.filter(item => item.id === selectedC.id);
    }
    return filteredCItems;
  };
    
    // Handle table change
    const handleTableChange = (table) => {
      setActiveTable(table);
      // Reset selections when changing tables
      setSelectedA(null);
      setSelectedB(null);
      setSelectedC(null);
      setSelectedD(null);
      setShowCItems(false);
      setShowDItems(table === 'doctoral');
      setFilteredAItems(null);
      setSelectedInfo("");
      setSelectionOrder([]);
      
      if (table === 'doctoral') {
        // For doctoral table: don't show B items initially
        setShowBItems(false);
      } else {
        // For other tables: show B items by default
        setShowBItems(true);
      }
    };

    // Update C-item click handler to show D items
    const handleCItemClick = (item) => {
      if (selectedC?.id === item.id) {
        setSelectedC(null);
        // Only deselect the C-item, don't hide the column or reset other selections
        return;
      }
      setSelectedC(item);
      setShowCItems(true); // Ensure C-items are visible when selected
      
      if (activeTable === 'doctoral') {
        // For doctoral table, auto-select corresponding A and B items based on University and Expertise
        const { aItems, bItems } = getActiveData();
        
        // Find and select corresponding A item (University)
        const correspondingA = aItems.find(a => a.id === item.University);
        if (correspondingA) {
          setSelectedA(correspondingA);
        }
        
        // Find and select corresponding B item (Expertise)
        const correspondingB = bItems.find(b => b.id === item.Expertise);
        if (correspondingB) {
          setSelectedB(correspondingB);
          // Don't show B items when C is selected - C replaces B in doctoral table
        }
        
        // Don't auto-select D item when clicking C - let user manually select D if needed
      } else {
        // Auto-select corresponding A and B items based on C-item's info for other tables
        if (item.info) {
          const { aItems, bItems } = getActiveData();
          const infoParts = item.info.split('_');
          
          if (infoParts.length >= 2) {
            const bId = infoParts[0]; // e.g., "B1"
            const aId = infoParts[1]; // e.g., "A1"
            
            // Find and select corresponding B item
            const correspondingB = bItems.find(b => b.id === bId);
            if (correspondingB) {
              setSelectedB(correspondingB);
              setShowBItems(true);
            }
            
            // Find and select corresponding A item
            const correspondingA = aItems.find(a => a.id === aId);
            if (correspondingA) {
              setSelectedA(correspondingA);
            }
          }
        }
      }
    };

    // Handle D-item click
    const handleDItemClick = (item) => {
      if (selectedD?.id === item.id) {
        setSelectedD(null);
        setFilteredAItems(null); // Reset filtered A items
        // Reset other selections when deselecting D
        setSelectedA(null);
        setSelectedB(null);
        setSelectedC(null);
        setShowCItems(false);
        setShowBItems(false);
        return;
      }
      setSelectedD(item);
      
      // Get all B-items from the active data
      const { bItems } = getActiveData();
      
      // Find all connections that include this D-item
      const dConnections = [];
      bItems.forEach(bItem => {
        bItem.connections.forEach(conn => {
          if (conn.d === item.id) {
            dConnections.push(conn);
          }
        });
      });
      
      // Extract unique A_IDs from these connections
      const connectedAIds = [...new Set(dConnections.map(conn => conn.a))];
      
      // Filter A items based on these connections
      if (connectedAIds.length > 0) {
        const { aItems } = getActiveData();
        const relevantAItems = aItems.filter(a => connectedAIds.includes(a.id));
        setFilteredAItems(relevantAItems);
      } else {
        // If no connections found, don't filter (or optionally show none)
        setFilteredAItems([]);
      }
      
      // Reset other selections but keep D selected
      setSelectedA(null);
      setSelectedB(null);
      setSelectedC(null);
      setShowCItems(false);
      setShowBItems(false); // Don't show B items until A is selected
    };



    return (
      <div className="app">
        {/* Modal for image popup */}
        {isModalOpen && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={closeModal}
          >
            <div 
              style={{
                position: 'relative',
                maxWidth: '90%',
                maxHeight: '90%'
              }}
              onClick={closeModal}
            >
              <img 
                src={modalImageSrc}
                alt="Full size view"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                }}
                onClick={e => e.stopPropagation()}
              />
              <button 
                onClick={closeModal}
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >×
              </button>
            </div>
          </div>
        )}
        {/* Navigation Header - same structure as NewPage */}
        <div className="navigation-header">
          <div className="left-section">
            <button 
              className="back-button"
              onClick={goToNewPage}
            >
              韩语授课
            </button>
            <img 
              src="/assets/icon.png" 
              alt="Icon" 
              className="header-icon"
            />
          </div>
          
          {/* Search Component - same as NewPage */}
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="搜索大学、专业..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onBlur={handleSearchBlur}
              onFocus={() => searchQuery && setShowSearchResults(true)}
            />
            <div className="search-icon">🔍</div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="search-result-item"
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <div className="result-type">{getResultTypeLabel(result.type)}</div>
                    <div className="result-university">{result.university}</div>
                    <div className="result-match">{result.match}</div>
                  </div>
                ))}
              </div>
            )}
            
            {/* No Results Message */}
            {showSearchResults && searchResults.length === 0 && searchQuery && (
              <div className="search-results">
                <div className="no-results">未找到相关结果</div>
              </div>
            )}
          </div>
          
          <div className="profile-switcher">
            <button 
              className={`nav-button ${activeTable === '3+1' ? 'active' : ''}`}
              style={{
                backgroundColor: activeTable === '3+1' ? '#007bff' : 'transparent',
                borderColor: '#007bff',
                color: activeTable === '3+1' ? 'white' : '#007bff'
              }}
              onClick={() => handleTableChange('3+1')}
            >
              3+1专升本
            </button>
            <button 
              className={`nav-button ${activeTable === '2-years' ? 'active' : ''}`}
              style={{
                backgroundColor: activeTable === '2-years' ? '#28a745' : 'transparent',
                borderColor: '#28a745',
                color: activeTable === '2-years' ? 'white' : '#28a745'
              }}
              onClick={() => handleTableChange('2-years')}
            >
              3+2专升本
            </button>
            <button 
              className={`nav-button ${activeTable === 'doctoral' ? 'active' : ''}`}
              style={{
                backgroundColor: activeTable === 'doctoral' ? '#dc3545' : 'transparent',
                borderColor: '#dc3545',
                color: activeTable === 'doctoral' ? 'white' : '#dc3545'
              }}
              onClick={() => handleTableChange('doctoral')}
            >
              硕士博士
            </button>
            <a
              href="https://repov2.umikaze.me"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-button"
              style={{
                borderColor: '#6c757d',
                color: '#6c757d'
              }}
              title="打开新版本（korean-university-v2）"
            >
              新版本（v2）
            </a>
          </div>
        </div>

        <div style={{padding: '20px'}}>

        
        <div className="content">
          {activeTable === 'doctoral' ? (
            // Dynamic layout for Doctoral table
            <div className="dynamic-layout">
              {/* A-items column - 50% initially, 20% when A or D selected */}
              <div className={`column a-column ${selectedA ? 'with-selection' : ''}`}>
                <h2>大学列表</h2>
                <ItemList
                  items={filteredAItems || aItems}
                  onItemClick={handleAItemClick}
                  selectedItem={selectedA}
                  disabledItems={selectedD && !selectedA ? [] : []} // Allow A selection even when D is selected
                />
                
                
              </div>
              
              {/* B-items column - only show when A is selected */}
              {selectedA && showBItems && (
                <div className="column b-column b-column-extended">
                  <h2>专业列表</h2>
                  <ItemList
                    items={getFilteredBItems()}
                    onItemClick={handleBItemClick}
                    selectedItem={selectedB}
                    disabledItems={[]}
                  />
                </div>
              )}
              
              {/* C-items column - 60% width, replaces B-column when B-item is selected */}
              {showCItems && (
                <div className="column c-column">
                  <h2>课程列表</h2>
                  <ItemList
                    items={getCItems()}
                    onItemClick={handleCItemClick}
                    selectedItem={selectedC}
                    selectedInfo={selectedInfo}
                  />
                  {selectedC && (
                    <div className="c-details-container" style={{marginTop: '20px'}}>
                      <div className="c-sub-columns" style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                        {/* Left sub-column: A-info, ranking */}
                        <div className="c-sub-column-left" style={{flex: 1, padding: '10px', background: '#f0f8ff', borderRadius: '5px'}}>
                          <h4>大学信息</h4>
                          {selectedA && (
                            <>
                              <p><strong>大学:</strong> {selectedA.name}</p>
                              <p><strong>排名:</strong> {selectedA.ranking || '未提供'}</p>
                              {selectedA.info && <p><strong>详情:</strong> {selectedA.info}</p>}
                            </>
                          )}
                        </div>
                        {/* Middle sub-column: B-End_date, Dormitory, Scholarship, B_fee, B_fee_name */}
                        <div className="c-sub-column-middle" style={{flex: 1, padding: '10px', background: '#f0fff0', borderRadius: '5px'}}>
                          <h4>专业信息</h4>
                          {selectedB && (
                            <>
                              <p><strong>截止日期:</strong> {selectedB.end_date || '未提供'}</p>
                              <p><strong>宿舍费用:</strong> {selectedB.dormitory || '未提供'}</p>
                              <p><strong>奖学金:</strong> {selectedB.scholarship || '未提供'}</p>
                              <p><strong>额外费用:</strong> {selectedB.B_fee || '无'}</p>
                              {selectedB.B_fee_name && <p><strong>费用说明:</strong> {selectedB.B_fee_name}</p>}
                            </>
                          )}
                        </div>
                        {/* Right sub-column: A_fee, sche, prerequisites, interview, camp */}
                        <div className="c-sub-column-right" style={{flex: 1, padding: '10px', background: '#fff8f0', borderRadius: '5px'}}>
                          <h4>课程信息</h4>
                          <p><strong>学费:</strong> {selectedC.A_Fee || '未提供'}</p>
                          <p><strong>学制:</strong> {selectedC.Sche || '未提供'}</p>
                          <p><strong>前置要求:</strong> {selectedC.no_prerequisites ? '无要求' : '有要求'}</p>
                          <p><strong>面试:</strong> {selectedC.Interview ? '需要' : '不需要'}</p>
                          <p><strong>校区:</strong> {selectedC.Camp || '未提供'}</p>
                        </div>
                      </div>
                      
                    </div>
                  )}
              </div>
            )}
            
            {/* D-items column - 50% initially, 20% when A or D selected */}
            {showDItems && (
              <div className={`column d-column ${selectedA ? 'with-selection' : ''}`}>
                <h2>学位列表</h2>
                <ItemList
                  items={dItems}
                  onItemClick={handleDItemClick}
                  selectedItem={selectedD}
                  disabledItems={[]}
                />
              </div>
            )}
          </div>
        ) : (
          // Grid layout for other tables
          <>
            <div className="main-content-container">
              {/* Selected University/Major at top when something is selected */}
              {(selectedA || selectedB) && (
                <div className="selected-item-container">
                  <button className="back-button" onClick={() => {
                    // Step-by-step back navigation
                    if (selectedC) {
                      // If C is selected, go back to first selected item's state
                      setSelectedC(null);
                      setShowCItems(false);
                      if (selectionOrder[0] === 'B') {
                        // B-A-C: back to B selected stage
                        setSelectedA(null);
                        setSelectionOrder(['B']);
                      } else {
                        // A-B-C: back to A selected stage
                        setSelectedB(null);
                        setShowBItems(true);
                        setSelectionOrder(['A']);
                      }
                    } else if (selectedB && selectedA) {
                      // If both B and A are selected, go back to first selected item's state
                      if (selectionOrder[0] === 'B') {
                        // B-A: back to B selected stage
                        setSelectedA(null);
                        setSelectionOrder(['B']);
                      } else {
                        // A-B: back to A selected stage
                        setSelectedB(null);
                        setShowBItems(true);
                        setSelectionOrder(['A']);
                      }
                    } else if (selectedB) {
                      // If only B is selected, go back to A level
                      setSelectedB(null);
                      setShowBItems(true);
                      setSelectionOrder([]);
                    } else if (selectedA) {
                      // If only A is selected, go back to start
                      setSelectedA(null);
                      setFilteredAItems(null);
                      setSelectionOrder([]);
                    }
                  }}>
                    ← 
                  </button>
                  <div className="selected-item-button">
                    <div className="selected-item-content">
                      <h3>
                         {selectedA && selectedB ? 
                           (selectionOrder[0] === 'B' ? `${selectedB.name} - ${selectedA.name}` : `${selectedA.name} - ${selectedB.name}`) : 
                           selectedB ? selectedB.name : 
                           selectedA ? selectedA.name : ''}
                       </h3>
                       <p>
                         {selectedA && selectedB ? 
                           (selectionOrder[0] === 'B' ? '专业 - 大学' : '大学 - 专业') : 
                           selectedB ? '专业' : 
                           selectedA ? '大学' : ''}
                       </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Initial state: Show both A and B buttons side by side */}
              {!selectedA && !selectedB && (
                <div className="initial-selection-container">
                  <div className="universities-section">
                    <h2>大学列表</h2>
                    <div className="universities-grid">
                      {aItems.map(item => (
                        <button
                          key={item.id}
                          className={`university-button ${item.end === 'T' ? 'end-date-t' : ''}`}
                          onClick={() => handleAItemClick(item)}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="majors-section">
                    <h2>学科开设</h2>
                    <div className="majors-grid">
                      {bItems.map(item => (
                        <button
                          key={item.id}
                          className="major-button"
                          onClick={() => handleBItemClick(item)}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Universities section - shows when B is selected */}
              {selectedB && !selectedA && (
                <div className="universities-section">

                  <div className="universities-grid">
                    {getConnectedAItems(selectedB.id).map(item => (
                      <button
                        key={item.id}
                        className={`university-button ${selectedA && selectedA.id === item.id ? 'selected' : ''} ${item.end === 'T' ? 'end-date-t' : ''}`}
                        onClick={() => handleAItemClick(item)}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Majors section - shows when A is selected */}
              {selectedA && !selectedB && showBItems && (
                <div className="majors-section">

                  <div className="majors-grid">
                    {getFilteredBItems().map(item => (
                      <button
                        key={item.id}
                        className={`major-button ${selectedB && selectedB.id === item.id ? 'selected' : ''}`}
                        onClick={() => handleBItemClick(item)}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* C-items section - shows when B is selected */}
              {selectedB && showCItems && (
                <div className="courses-section">
                  <div className="courses-grid">
                    {getCItems().map(item => (
                      <button
                        key={item.id}
                        className={`course-button ${selectedC && selectedC.id === item.id ? 'selected' : ''} ${item.no_prerequisites ? 'no-prerequisites' : ''}`}
                        onClick={() => handleCItemClick(item)}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            

            
            {showCItems && (
              <div className="c-items-row" style={{marginTop: '0px', display: 'flex', gap: '20px'}}>
                {/* Left Column - University Info */}
                 <div className="column" style={{flex: '1'}}>
                   <h2>{selectedA ? `${selectedA.name} 基本信息` : ''}</h2>
                   
                   {selectedA && (
                     <div style={{padding: '10px', background: '#f5f5f5', borderRadius: '8px'}}>
                       <h3>大学详情</h3>
                       <p>{selectedA.info || '暂无详细信息'}</p>
                     </div>
                   )}
                 </div>
                 
                 {/* Middle Column - Dormitory, Fees, Date and Images */}
                 <div className="column" style={{flex: '1'}}>
                   <h2>{selectedA ? `${selectedA.name} 费用信息` : ''}</h2>
                   
                   {selectedA && (
                     <div style={{padding: '10px', background: '#f5f5f5', borderRadius: '8px'}}>
                       <h3>报名截止日期</h3>
                       <p>{selectedA.end_date}</p>
                       
                       <h3>学杂费用</h3>
                       <p>{selectedA.B_Fee || "无"}</p>
                       <p>{selectedA.B_Fee_2}</p>
                       
                       <h3>宿舍费用</h3>
                       <p>{selectedA.Dormitory}</p>
                       
                       <div style={{marginBottom: '15px'}}>
                         {/* First row - images 1, 2, 3 */}
                         <div style={{display: 'flex', gap: '5%', marginBottom: '10px'}}>
                           {[1, 2, 3].map(num => {
                             const imageSrc = `/assets/${selectedA.name}${num}.png`;
                             return (
                               <img 
                                 key={num}
                                 src={imageSrc} 
                                 alt={`${selectedA.name}的图片${num}`} 
                                 style={{
                                   width: '30%',
                                   height: 'auto', 
                                   borderRadius: '5px',
                                   objectFit: 'cover',
                                   cursor: 'pointer'
                                 }} 
                                 onClick={() => openModal(imageSrc)}
                                 onError={(e) => {
                                   e.target.style.display = 'none';
                                 }}
                               />
                             );
                           })}
                         </div>
                         
                         {/* Second row - remaining images */}
                         <div style={{display: 'flex', gap: '5%', flexWrap: 'wrap'}}>
                           {[4, 5, 6, 7].map(num => {
                             const imageSrc = `/assets/${selectedA.name}${num}.png`;
                             return (
                               <img 
                                 key={num}
                                 src={imageSrc} 
                                 alt={`${selectedA.name}的图片${num}`} 
                                 style={{
                                   width: '30%',
                                   height: 'auto', 
                                   borderRadius: '5px',
                                   objectFit: 'cover',
                                   marginBottom: '10px',
                                   cursor: 'pointer'
                                 }} 
                                 onClick={() => openModal(imageSrc)}
                                 onError={(e) => {
                                   e.target.style.display = 'none';
                                 }}
                               />
                             );
                           })}
                         </div>
                       </div>
                     </div>
                   )}
                 </div>
                
                {/* Right Column - Course Info */}
                <div className="column" style={{flex: '1'}}>
                  <h2>{selectedB && selectedA ? `${selectedA.name}${selectedB.name}` : ''}</h2>
                  
                  {selectedC && (
                    <div className="c-info-tab" style={{marginTop: '20px', padding: '15px', background: '#eef', borderRadius: '8px'}}>
                      <h3>{selectedC.name} 的详情 </h3>
                      {selectedC.extra_info && <p><strong>学费详情:</strong> {selectedC.extra_info}</p>}
                      {selectedC.info2 && <p><strong>授予学士:</strong> {selectedC.info2}</p>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
        </div>
    </div>
  );
}

export default App;
